// Schéma du CONTENU pédagogique (« curriculum as code », docs/specs-fonctionnelles.md §8)
// et types du bundle compilé que reçoit le navigateur.
// Le contenu brut (Markdown + YAML) est validé avec ces schémas par `npm run contenu:valider`
// et par le serveur au démarrage. Toute erreur bloque : mieux vaut un contenu refusé
// qu'une session cassée un lundi soir.
import { z } from 'zod';

export const schemaDomaine = z.enum(['AG', 'EL', 'PR', 'LA', 'IA', 'CA']);
export const NOMS_DOMAINES: Record<z.infer<typeof schemaDomaine>, string> = {
  AG: 'Agilité et produit',
  EL: 'Électronique',
  PR: 'Programmation',
  LA: 'Langages',
  IA: 'IA : compréhension technique',
  CA: 'Coder avec l’IA',
};

/** saison.yml */
export const schemaSaison = z.object({
  numero: z.number().int().min(1).max(4),
  titre: z.string().min(1),
  objectif: z.string().min(1),
  niveau_ia: z.number().int().min(1).max(4),
  /** Ce que l'IA a le droit de faire cette saison, en une phrase (R-PRG-06). */
  regle_ia: z.string().min(1),
  mode_agile: z.enum(['kanban', 'scrum']),
  livrable: z.object({ titre: z.string(), description: z.string(), jalons: z.array(z.string()).default([]) }),
  glossaire: z.array(z.object({ terme: z.string(), explication: z.string(), exemple: z.string().optional() })).default([]),
});

/** semaine.yml */
export const schemaSemaine = z.object({
  numero: z.number().int().min(1).max(11),
  theme: z.string().min(1),
  defi: z.object({
    titre: z.string(),
    enonce: z.string(),
    indices: z.array(z.string()).default([]),
    criteres: z.array(z.string()).default([]),
  }),
  soutenance: z.object({
    titre: z.string(),
    role_mentor: z.string(),
    questions: z.array(z.string()).default([]),
    obligatoire: z.boolean().default(false),
  }).optional(),
});

const schemaQuiz = z.object({
  question: z.string(),
  choix: z.array(z.string()).min(2).max(5),
  /** Index (0-based) de la bonne réponse. */
  bonne: z.number().int().min(0),
  explication: z.string(),
});

/**
 * Une NOTION enseignée par la leçon (R-MEM-01). La règle qui la motive : rien de ce qu'une
 * étape ou un exercice demande ne doit apparaître pour la première fois au palier 2 — sinon
 * l'apprenant va chercher dehors, et la séance déborde sans qu'on le voie.
 * Ces fiches alimentent le mémo cumulatif ; le cours narratif reste dans la leçon.
 */
export const schemaNotion = z.object({
  /** Le nom tel qu'on le chercherait : `dictionnaire.get(cle, defaut)`, `f-string`, `sys.exit`. */
  nom: z.string().min(1).max(120),
  /** Quand on s'en sert, en une phrase. */
  quand: z.string().min(1).max(500),
  /** La forme, sur une ou deux lignes. */
  syntaxe: z.string().min(1).max(500),
  /** Un exemple exécutable, avec ce qu'il produit en commentaire. */
  exemple: z.string().min(1).max(2000),
  /** Ce qui fait trébucher, s'il y a lieu. */
  piege: z.string().max(1000).optional(),
  /**
   * Langage des blocs `syntaxe` et `exemple`, pour la coloration. Déduit du contenu quand il
   * n'est pas écrit : une ligne qui commence par `$` ou par une commande connue donne `bash`.
   */
  langage: z.string().max(20).optional(),
});

/** Frontmatter d'une session (session-NN.md). */
export const schemaSessionBrute = z.object({
  id: z.string().regex(/^S[1-4]-\d{2}$/),
  titre: z.string().min(1),
  domaines: z.array(schemaDomaine).min(1),
  duree: z.number().int().default(60),
  /** « Ce soir tu vas faire marcher … » (R-SES-01). */
  resultat_visible: z.string().min(10),
  /** Leçon théorique lue AVANT de pratiquer (fichier lecon-NN.md) : « ce que tu vas faire, et pourquoi ». */
  lecon: z.string().min(1),
  etapes: z.array(z.object({ titre: z.string(), consigne: z.string(), resultat: z.string() })).min(1),
  capsules: z.array(z.object({ id: z.string(), titre: z.string(), minutes: z.number().min(1).max(5), fichier: z.string() })).default([]),
  exercice: z.string().min(1),
  cartes: z.array(z.object({ recto: z.string(), verso: z.string(), domaine: schemaDomaine.optional() })).min(2),
  faible_energie: z.object({
    lecture_commentee: z.string(),
    quiz: z.array(schemaQuiz).min(3).max(5),
    diff: z.string().optional(),
  }),
  commit_attendu: z.string().optional(),
  /**
   * Les notions de langage ou d'outil enseignées ce soir (R-MEM-01). Au moins une — mais
   * la contrainte est portée par le contrôle STRICT plutôt que par le schéma, le temps que
   * les leçons écrites avant le 30/09/2026 soient complétées une par une.
   */
  notions: z.array(schemaNotion).default([]),
  /** Termes du glossaire de la saison à réviser avec cette session. */
  glossaire: z.array(z.string()).default([]),
});

/**
 * Frontmatter d'une session de PARCOURS ANNEXE (R-PAR-01) : même forme qu'une session de saison,
 * mais un identifiant `EL-03` au lieu de `S1-25`, et aucune semaine — un parcours n'a pas de
 * calendrier. Voir ADR 0009.
 */
export const schemaSessionParcoursBrute = schemaSessionBrute.extend({
  id: z.string().regex(/^[A-Z]{2}-\d{2}$/),
});

/** parcours.yml (R-PAR-01). */
export const schemaParcours = z.object({
  id: z.string().regex(/^[a-z][a-z0-9-]*$/),
  titre: z.string().min(1),
  objectif: z.string().min(1),
  description: z.string().min(1),
  livrable: z.object({ titre: z.string(), description: z.string(), jalons: z.array(z.string()).default([]) }),
  /** Défis facultatifs du parcours : un cran au-dessus, comme ceux du vendredi. */
  defis: z.array(z.object({
    titre: z.string(),
    enonce: z.string(),
    indices: z.array(z.string()).default([]),
    criteres: z.array(z.string()).default([]),
  })).default([]),
  glossaire: z.array(z.object({ terme: z.string(), explication: z.string(), exemple: z.string().optional() })).default([]),
});

/** Frontmatter d'un exercice (exercice-NN.md) ; le corps porte les sections balisées. */
export const schemaExerciceBrut = z.object({
  /** `S1-E07` dans une saison, `EL-E03` dans un parcours annexe (R-PAR-01). */
  id: z.string().regex(/^(S[1-4]|[A-Z]{2})-E\d{2}$/),
  titre: z.string().min(1),
  tests: z.string().optional(),
});

export const SECTIONS_EXERCICE = ['enonce', 'resultat_attendu', 'palier_1', 'palier_2', 'palier_3', 'palier_4', 'variante'] as const;
export type SectionExercice = (typeof SECTIONS_EXERCICE)[number];
export const TITRES_SECTIONS: Record<SectionExercice, string> = {
  enonce: 'Énoncé',
  resultat_attendu: 'Résultat attendu',
  palier_1: 'Palier 1',
  palier_2: 'Palier 2',
  palier_3: 'Palier 3',
  palier_4: 'Palier 4',
  variante: 'Variante',
};

/** Frontmatter d'une capsule ou d'une lecture commentée. */
export const schemaCapsuleBrute = z.object({
  id: z.string().min(1),
  titre: z.string().min(1),
  minutes: z.number().min(1).max(5).optional(),
});

/**
 * Frontmatter d'une leçon théorique (lecon-NN.md). Même forme qu'une capsule, mais une leçon
 * s'étale : les capsules sont des rappels « juste à temps » de 5 minutes au plus, la leçon est
 * la lecture d'ouverture de la séance (10 min visées, 20 au maximum).
 */
export const schemaLeconBrute = z.object({
  id: z.string().min(1),
  titre: z.string().min(1),
  minutes: z.number().min(5).max(40).optional(),
});

// ---------------------------------------------------------------------------
// Bundle compilé : ce que le serveur envoie et que le navigateur garde hors ligne.
// Tout le Markdown y est déjà converti en HTML.
// ---------------------------------------------------------------------------

export interface CapsuleCompilee { id: string; titre: string; minutes: number; html: string }

export interface ExerciceCompile {
  id: string;
  titre: string;
  tests?: string;
  /** HTML par section : enonce, resultat_attendu, palier_1 … palier_4, variante. */
  sections: Record<SectionExercice, string>;
}

export interface CarteCompilee { id: string; recto: string; verso: string; domaine?: string; session: string; glossaire?: boolean }

/** Une notion, telle que le mémo l'affiche (R-MEM-02). Tout est déjà en HTML, code colorié compris. */
export interface NotionCompilee {
  id: string;
  /** Le nom, en texte brut : il s'affiche dans un `<code>`, jamais interprété. */
  nom: string;
  quand_html: string;
  syntaxe_html: string;
  exemple_html: string;
  piege_html?: string;
  /**
   * Le texte brut de la fiche, pour la recherche du mémo. Chercher dans le HTML ferait
   * correspondre les balises de coloration : « span » trouverait toutes les fiches.
   */
  recherche: string;
  /** D'où elle vient, pour pouvoir relire le cours en entier. */
  session: string;
  titre_session: string;
  semaine: number;
}

export interface SessionCompilee {
  id: string;
  numero: number;
  /** Numéro de semaine de programme ; **0** pour une session de parcours annexe (R-PAR-01). */
  semaine: number;
  titre: string;
  domaines: string[];
  duree: number;
  resultat_visible: string;
  /** Corps de la session (introduction), en HTML. */
  html: string;
  /** La leçon théorique d'ouverture, compilée comme une capsule (titre, minutes, HTML). */
  lecon: CapsuleCompilee;
  etapes: { titre: string; consigne_html: string; resultat: string }[];
  capsules: CapsuleCompilee[];
  exercice: ExerciceCompile;
  cartes: string[];
  notions: NotionCompilee[];
  faible_energie: {
    lecture_commentee: CapsuleCompilee;
    quiz: z.infer<typeof schemaQuiz>[];
    diff_html?: string;
  };
  commit_attendu?: string;
}

export interface SemaineCompilee {
  numero: number;
  theme: string;
  defi: { id: string; titre: string; enonce_html: string; indices_html: string[]; criteres: string[] };
  soutenance?: { id: string; titre: string; role_mentor: string; questions: string[]; obligatoire: boolean };
  sessions: SessionCompilee[];
}

export interface BundleSaison {
  numero: number;
  titre: string;
  objectif: string;
  niveau_ia: number;
  regle_ia: string;
  mode_agile: 'kanban' | 'scrum';
  livrable: { titre: string; description: string; jalons: string[] };
  glossaire: { terme: string; explication: string; exemple?: string }[];
  semaines: SemaineCompilee[];
  /** Toutes les cartes de la saison, indexées par identifiant. */
  cartes: Record<string, CarteCompilee>;
  /** Empreinte du contenu (change à chaque modification) : sert au cache hors ligne (R-OFF-02). */
  empreinte: string;
  compile_le: string;
}

/**
 * Un PARCOURS ANNEXE compilé (R-PAR-01) : des sessions, sans semaine, sans défi et sans
 * soutenance. Il se lance quand on veut et ne compte pas dans les 33 sessions d'une saison
 * (R-PAR-03). Voir ADR 0009.
 */
export interface BundleParcours {
  id: string;
  titre: string;
  objectif: string;
  description: string;
  livrable: { titre: string; description: string; jalons: string[] };
  defis: { id: string; titre: string; enonce_html: string; indices_html: string[]; criteres: string[] }[];
  glossaire: { terme: string; explication: string; exemple?: string }[];
  sessions: SessionCompilee[];
  cartes: Record<string, CarteCompilee>;
  empreinte: string;
  compile_le: string;
}

/** Catalogue léger : ce que le client demande d'abord pour savoir quelles saisons existent. */
export interface CatalogueContenu {
  saisons: { numero: number; titre: string; empreinte: string; sessions: number }[];
  parcours: { id: string; titre: string; empreinte: string; sessions: number }[];
}
